import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link as MuiLink,
  InputAdornment,
  IconButton
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';
import ConstructionIcon from '@mui/icons-material/Construction';
import ForgotPasswordDialog from '../components/ForgotPasswordDialog';
import PWAInstallButton from '../components/PWAInstallButton';
import { prefetchGlobalData } from '../services/prefetchService';
import { HELP_LINKS } from '../constants/helpLinks';

const CORE_QR_THUMB = '/images/galldris-core-qr-plain.png';
const CORE_QR_DOWNLOAD = '/images/galldris-core-qr.png';

const PENDING_OTP_STORAGE_KEY = 'galldris_pending_otp';
const PENDING_OTP_EXPIRY_MS = 6 * 60 * 1000; // 6 min (backend OTP is 5 min)

function getStoredPendingOtp() {
  try {
    const raw = sessionStorage.getItem(PENDING_OTP_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.email || !data?.timestamp) return null;
    if (Date.now() - data.timestamp > PENDING_OTP_EXPIRY_MS) {
      sessionStorage.removeItem(PENDING_OTP_STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function clearStoredPendingOtp() {
  try {
    sessionStorage.removeItem(PENDING_OTP_STORAGE_KEY);
  } catch {}
}

export default function Login() {
  const appVersion = '1.7.0';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const { login, verifyLoginOTP } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const fromLocation = location.state?.from;
  const fromPath =
    fromLocation?.pathname
      ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
      : null;
  const posterPathname = fromLocation?.pathname || '';
  const projectIdFromPoster = posterPathname.startsWith('/site-induction-intro/')
    ? posterPathname.replace(/^\/site-induction-intro\//, '').split('/')[0]
    : null;

  // Restore OTP step from sessionStorage when user returns from checking email
  useEffect(() => {
    const stored = getStoredPendingOtp();
    if (stored?.email) {
      setEmail(stored.email);
      setOtpStep(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success && result.requiresOtp) {
        setOtpStep(true);
        setOtp('');
        try {
          sessionStorage.setItem(
            PENDING_OTP_STORAGE_KEY,
            JSON.stringify({ email, timestamp: Date.now() })
          );
        } catch {}
        toast.success('Verification code sent to your email');
      } else if (result.success) {
        toast.success('Login successful!');
        prefetchGlobalData().catch(() => {});
        navigate(fromPath || '/portal-selection', { replace: true });
      } else {
        setError(result.error);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'An unexpected error occurred';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await verifyLoginOTP(email, otp);
      if (result.success) {
        clearStoredPendingOtp();
        toast.success('Login successful!');
        prefetchGlobalData().catch(() => {});
        navigate(fromPath || '/portal-selection', { replace: true });
      } else {
        setError(result.error);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Verification failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToPassword = () => {
    clearStoredPendingOtp();
    setOtpStep(false);
    setOtp('');
    setError('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh' }}>
      {/* Left Side - Login Form */}
      <Box
        sx={{
          flex: { xs: '0 0 auto', md: '0 0 45%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 3, md: 4 },
          bgcolor: 'background.default',
          minHeight: { xs: 'auto', md: '100vh' },
          position: 'relative'
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            position: 'absolute',
            top: { xs: 12, md: 16 },
            right: { xs: 12, md: 16 },
            fontSize: { xs: '0.7rem', md: '0.75rem' },
            lineHeight: 1,
            zIndex: 1
          }}
        >
          Version v{appVersion}
        </Typography>
        <Box sx={{ width: '100%', maxWidth: 450, px: { xs: 2, md: 3 } }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 4, md: 6 }, justifyContent: 'center' }}>
            <Box
              component="img"
              src="/favicon.svg"
              alt="Galldris Core"
              sx={{ width: { xs: 40, md: 50 }, height: { xs: 40, md: 50 }, mr: 2 }}
            />
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'primary.main', fontSize: { xs: '1.5rem', md: '2.5rem' } }}>
              Galldris Core
            </Typography>
          </Box>

          {/* Login Header */}
          <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
              {otpStep ? 'Enter verification code' : 'Log In'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              {otpStep
                ? `We've sent a 6-digit code to ${email}. Enter it below.`
                : 'Access your construction management dashboard'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {otpStep ? (
            /* OTP verification form */
            <Box component="form" onSubmit={handleOtpSubmit}>
              <TextField
                fullWidth
                placeholder="Enter 6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                autoFocus
                sx={{ mb: 3 }}
                variant="outlined"
                inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || otp.length !== 6}
                sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600, mb: 2 }}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </Button>

              <MuiLink
                component="button"
                type="button"
                onClick={handleBackToPassword}
                underline="hover"
                sx={{ fontSize: '0.875rem', color: 'text.secondary', cursor: 'pointer', display: 'block', textAlign: 'center' }}
              >
                Back to login
              </MuiLink>
            </Box>
          ) : (
            /* Password login form */
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                placeholder="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                sx={{ mb: 2 }}
                variant="outlined"
              />

              <TextField
                fullWidth
                placeholder="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{ mb: 2 }}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword((p) => !p)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
                <MuiLink
                  href={HELP_LINKS.resetPasswordLogin}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  sx={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  How to reset password/login?
                </MuiLink>
                <MuiLink
                  onClick={() => setForgotPasswordOpen(true)}
                  underline="none"
                  sx={{
                    fontSize: '0.875rem',
                    color: 'error.main',
                    cursor: 'pointer',
                    '&:hover': { color: 'error.dark' }
                  }}
                >
                  Forgotten password?
                </MuiLink>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600, mb: 2 }}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Don't have an account?{' '}
                  <MuiLink
                    component={Link}
                    to={projectIdFromPoster ? { pathname: '/signup', search: `?projectId=${projectIdFromPoster}`, state: { projectId: projectIdFromPoster } } : '/signup'}
                    underline="hover"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 700,
                      fontSize: '1rem',
                      cursor: 'pointer'
                    }}
                  >
                    Sign up
                  </MuiLink>
                </Typography>
                <MuiLink
                  href={HELP_LINKS.signUpInduction}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  sx={{
                    display: 'inline-block',
                    mt: 0.75,
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  Need help? Watch the induction guide
                </MuiLink>
              </Box>
            </Box>
          )}

          {!otpStep && <PWAInstallButton variant="footer" />}

          {/* Site QR — download for posters / boards */}
          {!otpStep && (
            <Box
              sx={{
                mt: { xs: 1.5, md: 1.5 },
                py: 1,
                px: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: 'grey.50'
              }}
            >
              <Box
                component="img"
                src={CORE_QR_THUMB}
                alt="QR code for Galldris Core"
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: '#fff',
                  p: 0.25,
                  borderRadius: 0.75,
                  border: '1px solid',
                  borderColor: 'divider',
                  flexShrink: 0
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.3 }}>
                  Galldris Core QR
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3, mb: 0.5 }}>
                  Print for site boards - scan to open Core
                </Typography>
                <Button
                  component="a"
                  href={CORE_QR_DOWNLOAD}
                  download="galldris-core-qr.png"
                  size="small"
                  variant="text"
                  startIcon={<DownloadIcon sx={{ fontSize: '0.95rem !important' }} />}
                  sx={{
                    color: 'primary.main',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    p: 0,
                    minHeight: 0,
                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                  }}
                >
                  Download
                </Button>
              </Box>
            </Box>
          )}

          {/* Footer Links */}
          <Box sx={{ mt: { xs: 2.5, md: 3 }, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: { xs: 2, md: 3 }, alignItems: 'center' }}>
            <MuiLink component={Link} to="/privacy-policy" underline="none" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              Privacy Policy
            </MuiLink>
            <MuiLink component={Link} to="/support" underline="none" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              Support
            </MuiLink>
            <MuiLink component={Link} to="/cookie-policy" underline="none" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              Cookie Policy
            </MuiLink>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: { xs: 3, md: 4 }, textAlign: 'center', fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
            Copyright © 2026 Galldris Core. All rights reserved.
          </Typography>
        </Box>
      </Box>

      {/* Right Side - Hero Image (below form on mobile so users can scroll to see it) */}
      <Box
        sx={{
          flex: { xs: '0 0 auto', md: '0 0 55%' },
          minHeight: { xs: 420, md: '100vh' },
          position: 'relative',
          backgroundImage: 'url(/images/construction-aerial.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          overflow: 'hidden'
        }}
      >
        {/* Gradient Overlay for better text readability */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(17,87,64,0.85))'
          }}
        />

        {/* Modern Text Overlay */}
        <Box
          sx={{
            position: 'relative',
            p: { xs: 3, md: 6 },
            zIndex: 1
          }}
        >
          {/* Date Badge */}
          <Box 
            sx={{ 
              display: 'inline-block',
              bgcolor: 'rgba(248, 187, 0, 0.9)',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 1,
              mb: 3,
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              boxShadow: '0 4px 12px rgba(248, 187, 0, 0.3)'
            }}
          >
            {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </Box>

          {/* Main Headline */}
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 900, 
              color: 'white',
              mb: 3,
              lineHeight: 1.2,
              textShadow: '0 2px 20px rgba(0,0,0,0.5)',
              letterSpacing: -0.5,
              fontSize: { xs: '1.35rem', md: 'inherit' }
            }}
          >
            Streamline Your Construction Site Management
          </Typography>
          
          {/* Description */}
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'rgba(255,255,255,0.95)',
              mb: 4,
              lineHeight: 1.6,
              fontWeight: 400,
              maxWidth: '90%',
              textShadow: '0 1px 10px rgba(0,0,0,0.5)',
              fontSize: { xs: '0.95rem', md: 'inherit' }
            }}
          >
            Run inductions, raise and manage requisitions &amp; returns, record good and close calls, manage RAMS and inspections, oversee on-site asset management, deliver toolbox talks, and keep your site compliant - All in one powerful platform.
          </Typography>

          {/* Feature Tags */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ 
              bgcolor: 'rgba(255,255,255,0.15)', 
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white', 
              px: 3, 
              py: 1.5, 
              borderRadius: 2, 
              fontSize: '0.9rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}>
              <Box component="span" sx={{ fontSize: '1.2rem' }}>⚡</Box>
              Offline Support
            </Box>
            <Box sx={{ 
              bgcolor: 'rgba(255,255,255,0.15)', 
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white', 
              px: 3, 
              py: 1.5, 
              borderRadius: 2, 
              fontSize: '0.9rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}>
              <Box component="span" sx={{ fontSize: '1.2rem' }}>🔄</Box>
              Real-time Sync
            </Box>
            <Box sx={{ 
              bgcolor: 'rgba(255,255,255,0.15)', 
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white', 
              px: 3, 
              py: 1.5, 
              borderRadius: 2, 
              fontSize: '0.9rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}>
              <Box component="span" sx={{ fontSize: '1.2rem' }}>📱</Box>
              Mobile Ready
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog 
        open={forgotPasswordOpen} 
        onClose={() => setForgotPasswordOpen(false)} 
      />
    </Box>
  );
}

